/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Platform Templates Schemas & DTOs for Template Switcher
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Identifier of the application target for template switching.
 */
export const PlatformAppTargetSchema = z
  .enum(['webLanding', 'userWorkspace', 'adminPortal'])
  .openapi('PlatformAppTarget', {
    description: 'Target frontend application for template switching',
    example: 'webLanding',
  });

export type PlatformAppTarget = z.infer<typeof PlatformAppTargetSchema>;

/**
 * Metadata definition for an individual template.
 */
export const TemplateMetadataSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique template slug', example: 'default' }),
    name: z.string().openapi({ description: 'Human-readable template name', example: 'Default SaaS Modern' }),
    description: z.string().openapi({ description: 'Template description & features', example: 'Official Sophia Platform SaaS layout with Bento Grid.' }),
    author: z.string().openapi({ description: 'Author or Organization', example: 'Four Elements Corp' }),
    version: z.string().openapi({ description: 'Semantic version', example: '2.0.0' }),
    thumbnailUrl: z.string().openapi({ description: 'Preview screenshot thumbnail URL', example: '/assets/templates/landing-default.png' }),
    tags: z.array(z.string()).openapi({ description: 'Classification tags', example: ['saas', 'modern', 'bento'] }),
    isBuiltIn: z.boolean().openapi({ description: 'True if system built-in template (cannot be deleted)', example: true }),
    previewPath: z.string().openapi({ description: 'Relative path or query for live preview', example: '/?template=default' }),
  })
  .openapi('TemplateMetadata');

export type TemplateMetadata = z.infer<typeof TemplateMetadataSchema>;

/**
 * Template configuration for a specific application.
 */
export const AppTemplateConfigSchema = z
  .object({
    activeTemplate: z.string().openapi({ description: 'Currently active template ID', example: 'default' }),
    availableTemplates: z.array(TemplateMetadataSchema).openapi({ description: 'Catalog of registered templates' }),
  })
  .openapi('AppTemplateConfig');

export type AppTemplateConfig = z.infer<typeof AppTemplateConfigSchema>;

/**
 * Complete platform template configuration stored in D1 system_settings.
 */
export const PlatformTemplatesSettingSchema = z
  .object({
    webLanding: AppTemplateConfigSchema,
    userWorkspace: AppTemplateConfigSchema,
    adminPortal: AppTemplateConfigSchema,
    updatedAt: z.number().openapi({ description: 'Unix timestamp of last modification', example: 1798361200000 }),
    updatedBy: z.string().openapi({ description: 'Admin user ID who made the change', example: 'usr_admin_01' }),
  })
  .openapi('PlatformTemplatesSetting');

export type PlatformTemplatesSetting = z.infer<typeof PlatformTemplatesSettingSchema>;

/**
 * Input DTO for activating a template.
 */
export const ActivateTemplateInputSchema = z
  .object({
    app: PlatformAppTargetSchema,
    templateId: z.string().min(1, 'Template ID is required').openapi({ description: 'Template ID to activate', example: 'default' }),
  })
  .openapi('ActivateTemplateInput');

export type ActivateTemplateInput = z.infer<typeof ActivateTemplateInputSchema>;

/**
 * Public response schema for Edge clients querying active templates.
 */
export const PublicActiveTemplatesResponseSchema = z
  .object({
    webLanding: z.string().openapi({ description: 'Active template for web-landing', example: 'default' }),
    userWorkspace: z.string().openapi({ description: 'Active template for user-workspace', example: 'default' }),
    adminPortal: z.string().openapi({ description: 'Active template for admin-portal', example: 'default' }),
    timestamp: z.number().openapi({ description: 'Cache generation timestamp', example: 1798361200000 }),
  })
  .openapi('PublicActiveTemplatesResponse');

export type PublicActiveTemplatesResponse = z.infer<typeof PublicActiveTemplatesResponseSchema>;

/**
 * Default Initial Catalog of Built-In Templates across all 3 applications.
 * Single source of truth for Web Landing, User Workspace, and Admin Portal.
 */
export const DEFAULT_PLATFORM_TEMPLATES: PlatformTemplatesSetting = {
  webLanding: {
    activeTemplate: 'default',
    availableTemplates: [
      {
        id: 'default',
        name: 'Sophia Bento Modern (Default)',
        description: 'Trang tiếp thị chuẩn mực của Sophia Platform với Bento Grid 6 cards, so sánh tính năng và bảng giá.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/assets/templates/landing-default.png',
        tags: ['saas', 'modern', 'bento', 'high-conversion'],
        isBuiltIn: true,
        previewPath: '/?template=default',
      },
      {
        id: 'minimal',
        name: 'Minimalist Zen',
        description: 'Giao diện tối giản phẳng, tải trang siêu nhanh < 10ms, tập trung chuyển đổi dùng thử.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/assets/templates/landing-minimal.png',
        tags: ['minimal', 'lightweight', 'speed'],
        isBuiltIn: true,
        previewPath: '/?template=minimal',
      },
      {
        id: 'enterprise',
        name: 'Enterprise B2B Showcase',
        description: 'Bố cục đối soát an toàn thông tin, bảo mật SOC2/HIPAA, bảng giá doanh nghiệp lớn.',
        author: 'Four Elements Corp',
        version: '1.0.0',
        thumbnailUrl: '/assets/templates/landing-enterprise.png',
        tags: ['enterprise', 'b2b', 'compliance'],
        isBuiltIn: false,
        previewPath: '/?template=enterprise',
      },
      {
        id: 'fe-genai-classic',
        name: 'Four Elements AI — Classic Showcase (Home 1)',
        description: 'Bố cục Home 1 với Hero gõ chữ Typed.js, demo prompt nhanh, dải review trượt 2 chiều; tự thích ứng Dark/Light mode qua theme tokens.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/templates/fourelements-ai/images/thumbnails/1.jpg',
        tags: ['fourelements', 'classic', 'typewriter', 'adaptive-theme'],
        isBuiltIn: true,
        previewPath: '/?template=fe-genai-classic',
      },
      {
        id: 'fe-genai-modern',
        name: 'Four Elements AI — Modern Asymmetric (Home 2)',
        description: 'Bố cục Home 2 lệch tâm với floating badges, mockup nổi và tab giải pháp đa ngành; tự thích ứng Dark/Light mode qua theme tokens.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/templates/fourelements-ai/images/thumbnails/3.jpg',
        tags: ['fourelements', 'modern', 'tabs', 'adaptive-theme'],
        isBuiltIn: true,
        previewPath: '/?template=fe-genai-modern',
      },
    ],
  },
  userWorkspace: {
    activeTemplate: 'default',
    availableTemplates: [
      {
        id: 'default',
        name: 'Sophia Classic Studio (Default)',
        description: 'Bố cục chuẩn với thanh bên thu phóng 260px/68px và hỗ trợ trọn vẹn 20 Studio Views chuyên sâu.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/assets/templates/workspace-default.png',
        tags: ['studio', 'collapsible-sidebar', 'command-palette'],
        isBuiltIn: true,
        previewPath: '/workspace/documents?template=default',
      },
      {
        id: 'executive',
        name: 'Executive Command Matrix',
        description: 'Bố cục thanh điều hướng nằm ngang trên đỉnh, mở rộng tối đa không gian cho màn hình Ultra-wide.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/assets/templates/workspace-executive.png',
        tags: ['top-nav', 'executive', 'wide-screen'],
        isBuiltIn: true,
        previewPath: '/workspace/documents?template=executive',
      },
      {
        id: 'minimal-zen',
        name: 'Minimalist Zen Writer',
        description: 'Giao diện soạn thảo không xao nhãng, ẩn widget thừa, phóng đại khung chữ TipTap cho tác giả.',
        author: 'Four Elements Corp',
        version: '1.0.0',
        thumbnailUrl: '/assets/templates/workspace-zen.png',
        tags: ['zen', 'writer', 'focus'],
        isBuiltIn: false,
        previewPath: '/workspace/documents?template=minimal-zen',
      },
      {
        id: 'studio-immersive',
        name: 'Studio Immersive DAW',
        description: 'Bố cục tràn viền chia cột split-pane tối ưu cho Audio DAW Mixer, Video Studio, CAT Grid.',
        author: 'Four Elements Corp',
        version: '1.0.0',
        thumbnailUrl: '/assets/templates/workspace-daw.png',
        tags: ['immersive', 'daw', 'split-pane'],
        isBuiltIn: false,
        previewPath: '/workspace/voice/mixer?template=studio-immersive',
      },
    ],
  },
  adminPortal: {
    activeTemplate: 'default',
    availableTemplates: [
      {
        id: 'default',
        name: 'Default Mission Control Shell',
        description: 'Bố cục 2 cột cố định chuẩn mực cho Super Admin với 16 module quản trị và Recharts BI.',
        author: 'Four Elements Corp',
        version: '2.0.0',
        thumbnailUrl: '/assets/templates/admin-default.png',
        tags: ['mission-control', 'two-column', 'bi-charts'],
        isBuiltIn: true,
        previewPath: '/?template=default',
      },
      {
        id: 'cyberpunk-noc',
        name: 'Cyberpunk NOC Console',
        description: 'Bố cục trung tâm điều hành giám sát mạng tối màu Obsidian, viền neon phát sáng, footer Edge status.',
        author: 'Four Elements Corp',
        version: '1.0.0',
        thumbnailUrl: '/assets/templates/admin-cyberpunk.png',
        tags: ['cyberpunk', 'noc', 'dark-console'],
        isBuiltIn: false,
        previewPath: '/?template=cyberpunk-noc',
      },
      {
        id: 'executive-bi',
        name: 'Executive BI Dashboard',
        description: 'Bố cục dạng tường biểu đồ (TV Dashboard) phóng đại cho phòng họp điều hành doanh nghiệp.',
        author: 'Four Elements Corp',
        version: '1.0.0',
        thumbnailUrl: '/assets/templates/admin-bi.png',
        tags: ['executive-bi', 'tv-wall', 'charts'],
        isBuiltIn: false,
        previewPath: '/?template=executive-bi',
      },
      {
        id: 'compact',
        name: 'Compact Admin Console',
        description: 'Sidebar thu gọn 56px chỉ hiển thị biểu tượng, tối ưu hiển thị các bảng dữ liệu lớn.',
        author: 'Four Elements Corp',
        version: '1.0.0',
        thumbnailUrl: '/assets/templates/admin-compact.png',
        tags: ['compact', 'laptop', 'data-dense'],
        isBuiltIn: false,
        previewPath: '/?template=compact',
      },
    ],
  },
  updatedAt: 1798361200000,
  updatedBy: 'system',
};
